import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Avatar, Button, cn } from '@codecraftkit/theme'
import { ImageCrop } from './../ImageCrop'
import AlertInfo from '@codecraftkit/alert-info'
import { FileDrop } from 'react-file-drop'
import InputFiles from 'react-input-files'
import { MdCheck, MdAdd } from 'react-icons/md'

export const ProfilePictureUploadImage = ({ item, onCloseModal, imageField, defaultImage, uploadImageAction, getImage, aspect, isRounded, callback, closeModalOnSubmit }) => {
  /*
   * El error se muestra ACA, no por un toast. Ver la nota larga en @codecraftkit/layout-tools
   * NoAccess: llamaba `useToasts()` de react-toast-notifications, que se saco de las apps, y el
   * `useToast` de este monorepo tampoco sirve porque tambien tira sin provider. Una libreria no
   * puede exigirle al consumidor que lo haya montado.
   */
  const [error, setError] = useState(null)
  const [image, setImage] = useState({})
  const [isImageSet, setIsImageSet] = useState(false)
  const [loading, setLoading] = useState(false)
  /*
   * The crop starts EMPTY and is seeded by ImageCrop once the image knows its size — v11's
   * `makeAspectCrop` needs the rendered dimensions. The old shape carried `aspect` inside the
   * crop object, which is its own prop since v10.
   */
  const [crop, setCrop] = useState()
  const cropAspect = typeof aspect === 'number' ? aspect : 1

  //Set Image
  const cancelImage = () => {
    setImage({})
    setIsImageSet(false)
  }

  const handleConvertImageFileToUrl = (imageFile) => {
    const reader = new FileReader()
    reader.readAsDataURL(imageFile)
    reader.onloadend = () => {
      setImage({ data: reader.result, file: imageFile })
      setIsImageSet(true)
    }
  }

//generate random file name
  const getRandomNumber = (min, max) => Math.random() * (max - min) + min

  const getRandomString = (length) => [...Array(length)].map(() => (~~(Math.random() * 36)).toString(36)).join('')

  const generateRandomFileName = (filename) => Date.now() + getRandomNumber(0, 9999) + getRandomString(15) + '.' + filename.split('.').pop().toLowerCase()

  //handle Base64
  const decodeBase64 = (base64) => {
    const matches = base64.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/)
    const response = {}

    if (matches.length !== 3) {
      return new Error('Invalid input string')
    }

    response.type = matches[1]
    response.extension = matches[1].match(/\/(.*?)$/)[1]

    return response
  }

  const dataURLtoFile = (dataUrl, filename) => {
    let arr = dataUrl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bString = atob(arr[1]),
      n = bString.length,
      u8arr = new Uint8Array(n)

    while (n--) {
      u8arr[n] = bString.charCodeAt(n)
    }

    return new File([u8arr], filename, { type: mime })
  }

  //Upload Image
  const upload = async (img) => {
    try {
      getImage && getImage(img.croppedImage)
      const base64Info = decodeBase64(img.croppedImage)
      const filename = generateRandomFileName('.' + base64Info.extension.toLowerCase())
      const files = [dataURLtoFile(img.croppedImage, filename)]
      uploadImageAction && uploadImageAction(files, { setLoading, onCloseModal, callback, item, img })
      closeModalOnSubmit && onCloseModal && onCloseModal()
    } catch (e) {
        /*
         * `e?.message` y no `e`: el original pasaba el Error entero como contenido del aviso, y un
         * objeto como hijo de React tira "Objects are not valid as a React child" — el manejo del
         * error tenia su propio error, en el unico camino donde importaba.
         */
        setError(e?.message || 'Could not upload the image')
      setLoading(false)
      console.error(e)
    }
  }

  const preview = image?.croppedImage || image?.data || defaultImage || item?.[imageField]

  return (
    <div className='grid gap-y-4 grid-rows-[auto_1fr_auto] pt-4'>
      {error && <AlertInfo status='error' title={error} onClose={() => setError(null)} />}
      <div className='grid min-h-[300px]'>
        {isImageSet
          ? (
            <ImageCrop
              image={image}
              setImage={setImage}
              crop={crop}
              setCrop={setCrop}
              circularCrop={isRounded}
              aspect={cropAspect}
            />
            )
          : (
            <FileDrop onDrop={files => handleConvertImageFileToUrl(files[0])}>
              <div className='flex justify-center items-center h-[300px] max-w-[700px] mx-auto rounded-md border-4 border-dashed border-gray-400 bg-gray-50 dark:bg-gray-900'>
                <span>drop picture here</span>
              </div>
            </FileDrop>
            )}
      </div>

      {isImageSet
        ? <h3 className='text-base font-semibold'>Crop image</h3>
        : (
          <div className='flex justify-center'>
            <InputFiles onChange={files => handleConvertImageFileToUrl(files[0])} style={{ outline: 'none' }}>
              <Button size='sm'>
                <span className='inline-flex items-center gap-2'><MdAdd aria-hidden='true' />Upload Image</span>
              </Button>
            </InputFiles>
          </div>
          )}

      {isImageSet && (
        <div className='grid p-2 justify-center'>
          {isRounded
            ? <Avatar src={preview} size='xl' className='w-[200px] h-[200px] shadow-[0px_0px_7px_#718096]' />
            : <img src={preview} alt='Preview' className='w-[200px] h-[200px] object-cover shadow-[0px_0px_7px_#718096]' />}
        </div>
      )}

      <div className='grid grid-cols-[auto_auto] gap-2 justify-end'>
        {isImageSet && <Button onClick={cancelImage} size='xs' className='my-2'>Cancel</Button>}
        {/*
          * Disabled until there IS a cropped image, which is the only thing `upload` reads. The
          * old condition —`crop.width > 10 && image.croppedImage ? !isImageSet : true`— could
          * never be false, because nothing ever set `croppedImage`: the crop component rendered
          * an empty Fragment. Submit was permanently disabled.
          */}
        <Button
          onClick={() => upload(image)}
          isLoading={loading}
          isDisabled={!image?.croppedImage}
          size='xs'
          colorScheme='green'
          className='my-2'
        >
          <span className='inline-flex items-center gap-2'><MdCheck aria-hidden='true' />Submit</span>
        </Button>
      </div>
    </div>
  )
}

ProfilePictureUploadImage.propTypes = {
  item: PropTypes.object,
  onCloseModal: PropTypes.func,
  imageField: PropTypes.string,
  defaultImage: PropTypes.string,
  /** Receives `(files, { setLoading, onCloseModal, callback, item, img })`. */
  uploadImageAction: PropTypes.func,
  getImage: PropTypes.func,
  /** `1` for a square, `16 / 9` for landscape. */
  aspect: PropTypes.number,
  isRounded: PropTypes.bool,
  callback: PropTypes.func,
  closeModalOnSubmit: PropTypes.bool
}
